import fetchData from "@/lib/Fetchers/fetchData";
import {
  dateListResponse,
  dateListOffer,
} from "@/lib/Types/DateListResponse/DateListResponse";
import { sendKibanaLog } from "./sendKibanaLog";
import { addMinutes, format, startOfDay } from "date-fns";
import { AppConfig } from "@/lib/Types/AppConfig";
import { getIsLoggedIn } from "@/lib/Helpers/userHelpers";

function convertMinutesToTime(minutes: string | undefined): string | undefined {
  if (!minutes) return undefined;
  const totalMinutes = parseInt(minutes, 10);
  if (isNaN(totalMinutes)) return undefined;
  const date = addMinutes(startOfDay(new Date()), totalMinutes);
  return format(date, "HH:mm");
}

interface UpsellingOffersParams {
  cid: string;
  from: string; // search from (YYYY-MM-DD)
  to: string; // search to (YYYY-MM-DD)
  mainOfferFromDate: string; // offer from
  mainOfferToDate: string; // offer to
  rooms: string;
  tourOperatorCode: string;
  mealType: string; // mainOfferMealType
  roomType: string; // mainOfferRoomType
  roomName: string; // mainOfferRoomName
  transferIncluded: boolean;
  price: number; // mainOfferPrice
  flights: object; // will be URL-encoded
  searchId?: string;
  transactionId?: string;

  // Optional filter params (aligned with datelistsearch)
  app?: boolean;
  subsource?: string;
  sort?: string;
  filterMealType?: string;
  filterRoomType?: string;
  tourOperator?: string;
  utmCampaign?: string;
  directFlight?: boolean;
  transfer?: boolean;
  railAndFly?: boolean;
  rentalCar?: boolean;
  flexibleOffersExpected?: boolean;
  oceanView?: boolean;
  flexibleOffers?: boolean;
  bookOnOption?: boolean;
  priceFilter?: string;
  departureAirport?: string;
  categories?: string;
  specialDeals?: boolean;
  toVoucher?: boolean;
  departureFlightTimeFrom?: string;
  departureFlightTimeUntil?: string;
  returnFlightTimeFrom?: string;
  returnFlightTimeUntil?: string;
  duration?: string;

  // App config
  appConfig: AppConfig;
}

export async function fetchUpsellingOffers(
  params: UpsellingOffersParams,
): Promise<dateListOffer[]> {
  const { envConfig, viewMode, userData } = params.appConfig;

  const isDurationZero = params.duration === "0";

  // Determine specialDealTypes based on priority: toVoucher > specialDeals
  const specialDealTypes = params.toVoucher
    ? "TO_VOUCHER"
    : params.specialDeals
      ? "DISCOUNT_PRICE,TO_VOUCHER"
      : undefined;

  const paramsObject = {
    upsellingOffers: true,
    TourOperator: params.tourOperatorCode,
    cid: params.cid,
    channel: viewMode.channel,
    device: userData?.deviceType || "DESKTOP",
    from: params.from,
    to: params.to,
    rooms: params.rooms,
    p: viewMode.profile,
    m: viewMode.mandator,
    mainOfferFromDate: params.mainOfferFromDate,
    mainOfferToDate: params.mainOfferToDate,
    mainOfferMealType: params.mealType,
    mainOfferRoomType: params.roomType,
    mainOfferRoomName: params.roomName,
    mainOfferTransferIncluded: params.transferIncluded,
    mainOfferPrice: params.price,
    mainOfferFlights:
      params.flights && Object.keys(params.flights).length > 0
        ? encodeURIComponent(JSON.stringify(params.flights))
        : null,
    userUid: userData?.userUid || "",
    isLoggedIn: getIsLoggedIn(),
    multiroom: params.rooms.includes(";"),
    page: "1_5", // Get more offers for upselling
    platformName: "ICARUS",

    // Optional params - simple pass-through
    ...(params.searchId && { searchId: params.searchId }),
    ...(params.app !== undefined && { app: params.app }),
    ...(params.subsource && { subsource: params.subsource }),
    ...(params.sort && { sort: params.sort }),
    ...(params.filterMealType && { mealType: params.filterMealType }),
    ...(params.filterRoomType && { roomType: params.filterRoomType }),
    ...(params.tourOperator && { tourOperator: params.tourOperator }),
    ...(params.utmCampaign && { utmCampaign: params.utmCampaign }),

    // Boolean flags
    ...(params.directFlight && { directFlight: true }),
    ...(params.transfer && { transfer: true }),
    ...(params.railAndFly && { railAndFly: true }),
    ...(params.rentalCar && { rentalCar: true }),
    ...(params.flexibleOffersExpected && { flexibleOffersExpected: true }),
    ...(params.oceanView && { roomAddition: "OCEANVIEW" }),
    ...(params.flexibleOffers && { flexibleOffers: "flexibleCancellation" }),
    ...(params.bookOnOption && { bookOnOption: "true" }),

    // Transformed values
    ...(params.priceFilter && {
      price: `0_${parseInt(params.priceFilter).toFixed(0)}`,
    }),
    ...(params.departureAirport && {
      departureAirport: params.departureAirport.split(","),
    }),
    ...(params.categories && {
      categories: params.categories.split(","),
    }),
    ...(specialDealTypes && { specialDealTypes }),

    // Flight time filters (exclude 1440 which means "any time")
    ...(params.departureFlightTimeFrom && {
      departureFlightTimeFrom: convertMinutesToTime(
        params.departureFlightTimeFrom,
      ),
    }),
    ...(params.departureFlightTimeUntil &&
      params.departureFlightTimeUntil !== "1440" && {
        departureFlightTimeUntil: convertMinutesToTime(
          params.departureFlightTimeUntil,
        ),
      }),
    ...(params.returnFlightTimeFrom && {
      returnFlightTimeFrom: convertMinutesToTime(params.returnFlightTimeFrom),
    }),
    ...(params.returnFlightTimeUntil &&
      params.returnFlightTimeUntil !== "1440" && {
        returnFlightTimeUntil: convertMinutesToTime(
          params.returnFlightTimeUntil,
        ),
      }),

    // Handle duration logic: when duration is 0, use dateExact instead
    ...(isDurationZero
      ? { dateExact: true }
      : params.duration
        ? { duration: params.duration }
        : {}),
  };

  const url = `${envConfig.api}/comvel-datelistsearch-service/rest/datelistsearch`;

  sendKibanaLog({
    envConfig,
    transactionId: params.transactionId,
    message: "UPSELLING OFFERS START",
    component: "upselling",
    jsonMessage: paramsObject,
    searchId: params.searchId,
    category: "debug",
  });

  const data = await fetchData({
    url,
    method: "GET",
    params: paramsObject,
    envConfig,
    service: "upselling",
    transactionId: params.transactionId,
    searchId: params.searchId,
  });

  const isError = data?.apiError;

  sendKibanaLog({
    envConfig,
    transactionId: params.transactionId,
    message: `UPSELLING OFFERS ${isError ? "FAIL" : "SUCCESS"}`,
    component: "upselling",
    jsonMessage: isError ? data.message : { count: data.dateList?.length },
    searchId: params.searchId,
    category: "debug",
  });

  if (isError) {
    return [];
  }

  return (data as dateListResponse).dateList || [];
}
